import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Whether a scroll-triggered update is already queued for the next frame.
   * Prevents #handleWindowScroll from running more than once per rendered frame.
   * @type {boolean}
   */
  #scrollTicking = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Wrapper around #handleWindowScroll that limits it to running once per
   * rendered frame, instead of once per raw scroll event.
   */
  #onScroll = () => {
    if (this.#scrollTicking) return;
    this.#scrollTicking = true;
    requestAnimationFrame(() => {
      this.#handleWindowScroll();
      this.#scrollTicking = false;
    });
  };

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#onScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#onScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

/* ===== MENU INTERACTIONS ===== */
(function () {
  function getSubmenuForLink(link) {
    const parentLi = link.closest('li');
    if (!parentLi) return null;

    let submenu = link.nextElementSibling;
    if (
      submenu &&
      !submenu.classList.contains('menu-drawer__menu--childlist') &&
      !submenu.classList.contains('menu-drawer__menu--grandchildlist') &&
      !submenu.classList.contains('menu-drawer__submenu')
    ) {
      submenu = null;
    }

    if (!submenu) {
      submenu = parentLi.querySelector(
        ':scope > .menu-drawer__menu--childlist, :scope > .menu-drawer__menu--grandchildlist, :scope > .menu-drawer__submenu'
      );
    }

    if (!submenu && parentLi.parentElement?.tagName === 'DETAILS') {
      submenu = parentLi.parentElement.querySelector(
        '.menu-drawer__menu--childlist, .menu-drawer__menu--grandchildlist, .menu-drawer__submenu'
      );
    }

    return submenu || null;
  }

  function closeOtherDrawerParents(currentLink) {
    const drawer = currentLink.closest('.menu-drawer, .header__drawer');
    if (!drawer) return;

    drawer.querySelectorAll('[data-parent-link="true"]').forEach((otherLink) => {
      if (otherLink === currentLink) return;

      otherLink.classList.remove('active');
      otherLink.removeAttribute('aria-expanded');

      const submenu = getSubmenuForLink(otherLink);
      if (submenu) {
        submenu.style.display = 'none';
      }

      const detailsEl = otherLink.parentElement;
      if (detailsEl?.tagName === 'DETAILS') {
        detailsEl.open = false;
      }
    });
  }

  // ===== PARENT LINK TOGGLE FOR MOBILE DRAWER =====
  function setupDrawerParentLinkToggle() {
    const allMenuItems = document.querySelectorAll('.menu-drawer__menu-item');

    allMenuItems.forEach((link) => {
      const parentLi = link.closest('li');
      if (!parentLi) return;

      let hasChildren = false;

      const sibling = link.nextElementSibling;
      if (
        sibling &&
        (sibling.classList.contains('menu-drawer__menu--childlist') ||
          sibling.classList.contains('menu-drawer__menu--grandchildlist') ||
          sibling.classList.contains('menu-drawer__submenu'))
      ) {
        hasChildren = true;
      }

      if (link.querySelector('.icon-caret, .icon-plus')) {
        hasChildren = true;
      }

      if (
        link.closest('.menu-drawer__menu-container') ||
        link.tagName === 'SUMMARY' ||
        (link.classList.contains('menu-drawer__menu-item--mainlist') &&
          (link.classList.contains('menu-drawer__menu-item--active') || link.getAttribute('aria-expanded')))
      ) {
        const childUl = parentLi.querySelector(':scope > ul');
        if (childUl) {
          hasChildren = true;
        }
      }

      if (
        parentLi.classList.contains('menu-drawer__list-item--deep') &&
        link.classList.contains('menu-drawer__menu-item--parent')
      ) {
        hasChildren = true;
      }

      if (!hasChildren) {
        link.style.pointerEvents = 'auto';
        link.style.cursor = 'pointer';
        link.removeAttribute('data-parent-link');
        link.removeAttribute('data-drawer-toggle-attached');
        return;
      }

      link.setAttribute('data-parent-link', 'true');

      const submenu = getSubmenuForLink(link);
      if (!submenu) return;

      if (link.dataset.drawerToggleAttached === 'true') return;
      link.dataset.drawerToggleAttached = 'true';

      link.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const isActive =
            link.classList.contains('active') ||
            link.getAttribute('aria-expanded') === 'true' ||
            (link.parentElement.tagName === 'DETAILS' && link.parentElement.open);

          closeOtherDrawerParents(link);

          if (!isActive) {
            link.classList.add('active');
            link.setAttribute('aria-expanded', 'true');
            submenu.style.display = 'block';
            if (link.parentElement.tagName === 'DETAILS') {
              link.parentElement.open = true;
            }
          } else {
            link.classList.remove('active');
            link.removeAttribute('aria-expanded');
            submenu.style.display = 'none';
            if (link.parentElement.tagName === 'DETAILS') {
              link.parentElement.open = false;
            }
          }
        },
        true
      );
    });
  }

  // ===== DESKTOP MENU SETUP =====
  function setupDesktopParentLinks() {
    const desktopMenus = document.querySelectorAll(
      'header > ul, header nav > ul, header .menu > ul, header .main-menu > ul, header [role="navigation"] > ul, header .header__menu > ul'
    );

    desktopMenus.forEach((menu) => {
      let topLevelItems;
      try {
        topLevelItems = menu.querySelectorAll(':scope > li');
      } catch (error) {
        console.error('Failed to query top-level menu items:', error);
        return;
      }

      topLevelItems.forEach((li) => {
        const childList = li.querySelector(':scope > ul');
        if (!childList) return;

        li.classList.add('has-children');

        const link = li.querySelector(':scope > a');
        if (!link) return;

        link.setAttribute('data-parent-link', 'true');

        if (link.dataset.desktopToggleAttached === 'true') return;
        link.dataset.desktopToggleAttached = 'true';

        link.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const wasActive = li.classList.contains('active');
            desktopMenus.forEach((otherMenu) => {
              otherMenu.querySelectorAll('li.has-children.active').forEach((otherLi) => {
                if (otherLi !== li) {
                  otherLi.classList.remove('active');
                }
              });
            });

            if (!wasActive) {
              li.classList.add('active');
            } else {
              li.classList.remove('active');
            }
          },
          true
        );
      });
    });
  }

  // ===== COMBINED INITIALIZATION =====
  function initializeAllMenus() {
    setupDrawerParentLinkToggle();
    setupDesktopParentLinks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllMenus);
  } else {
    initializeAllMenus();
  }

  const menuDrawerEl = document.querySelector('.menu-drawer__menu');
  const desktopMenuEl = document.querySelector('.header-menu');
  const menuContainers = [menuDrawerEl, desktopMenuEl].filter(Boolean);

  if (menuContainers.length) {
    let reinitTimer = null;
    const scheduleReinit = () => {
      clearTimeout(reinitTimer);
      reinitTimer = setTimeout(initializeAllMenus, 50);
    };

    const observer = new MutationObserver((mutations) => {
      const needsReinit = mutations.some((mutation) => {
        if (mutation.type === 'attributes') {
          return (
            mutation.attributeName === 'data-parent-link' &&
            mutation.target.getAttribute('data-parent-link') === null
          );
        }
        return mutation.type === 'childList';
      });
      if (needsReinit) scheduleReinit();
    });

    menuContainers.forEach((container) => {
      observer.observe(container, {
        attributes: true,
        attributeFilter: ['data-parent-link'],
        childList: true,
        subtree: true,
      });
    });
  }
})();